// THE CATEGORY KIND VOCABULARY (Scott, 2026-10-10) — ONE place.
//
// Categories are per brand (their own rows, naming and order), but "Team" for Snap
// and "Team" for Fitstop are the SAME IDEA, so every category carries a `kind`: a
// slug from this fixed list. That is what lets the agency ask "show me every team
// photo" with one `categoryKinds array-contains 'team'` clause.
//
// ⚠️⚠️ A KIND IS SET AT CREATION AND IS NOT EDITABLE. Each asset stores a DENORMALISED
// copy of its categories' kinds (`PortalAsset.categoryKinds`), derived server-side
// when the asset's categories are set. Editing a category's kind would silently leave
// every asset already filed under it carrying the OLD kind, with nothing to say so.
// The only reason that denormalisation is safe is this immutability, so it is
// enforced in the service (categories.ts) and the zod schemas, not just the UI.
// Re-kinding a category later is a deliberate, attended migration — like a brand move.
//
// Adding a kind is one line here. REMOVING one would strand stored values, so don't.

export const CATEGORY_KINDS = [
  'team',
  'equipment',
  'classes',
  'facility',
  'events',
  'members',
  'promotions',
  // Added 2026-10-12 (Run E). ADD-ONLY: the first eight above are untouched, because every asset carries a denormalised copy.
  'exterior',
  'location',
  'props',
  'other',
] as const;

export type CategoryKind = (typeof CATEGORY_KINDS)[number];

export const CATEGORY_KIND_LABELS: Record<CategoryKind, string> = {
  team: 'Team',
  equipment: 'Equipment',
  classes: 'Classes',
  facility: 'Facility',
  events: 'Events',
  members: 'Members',
  promotions: 'Promotions',
  exterior: 'Exterior',
  location: 'Area / Landmark',
  props: 'Props & branding',
  other: 'Other',
};

export function isCategoryKind(value: unknown): value is CategoryKind {
  return typeof value === 'string' && (CATEGORY_KINDS as readonly string[]).includes(value);
}

/**
 * A category stored before kinds existed has none. It reads as `other` — the honest
 * "no specific kind" — rather than being guessed from its name; it is not written
 * back (a read never mutates), and kind stays non-editable.
 */
export function categoryKindOrOther(stored: unknown): CategoryKind {
  return isCategoryKind(stored) ? stored : 'other';
}
